import React from 'react';
import classnames from 'classnames';
import pick from 'lodash/pick';
import every from 'lodash/every';
import last from 'lodash/last';
import PAPBase from 'pap/containers/common/pap_base';
import PAPEditReviewSection from 'pap/containers/review_editor';
import { PAP_ANSWER_RENDERER_COMPONENTS } from 'pap/components/common/answer_renderers';
import { v4 } from 'node-uuid';
import Created1to1 from 'pap/components/created_1_1';
import { getProjectUrl } from 'project/utils';
import { getSubcategorySlug, getCategory } from 'common/ducks/categories';
import {
  trackGoogle, trackFacebookCustomEvent,
  trackMixpanel, trackClickWithMixpanel, MP_CHANGED_PAP_STEP_EVENT_NAME,
  MP_CHANGED_PAP_SECTION_EVENT_NAME
} from 'common/tracking';
import { getFieldPropsFromDefinition, getVisibleBriefQuestions } from 'pap/utils';

/**
 * This component does two things - it renders all project metadata and brief answers and lets the
 * user make changes to those (via PAPEditSection).
 *
 * Every change is immediately persisted to the server.
 *
 * @author Kamil Grymuza <kamil@startupfoundry.com>
 */

class PAPReview extends React.Component {

  redirectIfNeeded(project, categories) {
    /* redirect to project page if not on draft status */
    if (project.status != 'draft') {
      const subcategory = getSubcategorySlug(categories, project.sub_category);
      const url = getProjectUrl(project, subcategory);
      this.props.router.push(url);
    }
  }

  componentDidMount() {
    const { categories } = this.props;
    this.props.fetchProjectIfNeeded({projectId: this.props.projectID})
      .then(({data}) => {
        const subcategory = getSubcategorySlug(categories, data.sub_category);
        const category = getCategory(categories.data, data.category);
        this.props.fetchPricing(data.sub_category)
        trackGoogle("pap", "started review", subcategory, null, function() {
          // this will be always called
          this.redirectIfNeeded(data, categories)
        }.bind(this))

        trackMixpanel("Started PAP Review", {
          categorySlug: category.slug,
          subcategorySlug: subcategory,
        });
      });

    this.props.fetchCustomContracts(this.props.projectID);
    this.props.fetchAttachments(this.props.projectID);
    this.props.fetchCategoriesIfNeeded();
  }

  start1to1Negotiation() {
    const { updateProjectRequest, projectID, projectData, advanceNegotiationRequest } = this.props

    /* update project status and start negotiation */
    return updateProjectRequest(projectID, {status: '1_1_negotiating'})
      .then(() => {
        const negotiationValue = parseFloat(projectData.one_to_one_negotiation_value)
        return advanceNegotiationRequest(projectID, {
          next_status: negotiationValue? 'client_sent_offer':'client_requested_quote',
          next_value: negotiationValue || undefined,
          next_creative: projectData.one_to_one_creative
        })
      })
  }

  onPrevStep() {
    const { projectData, router, detailsSteps } = this.props;
    let prevUrl;

    if (projectData.is_one_to_one) {
      const lastBriefQuestion = last(getVisibleBriefQuestions(projectData, true)).name;
      prevUrl = `/start-a-project/${projectData.id}/brief/${lastBriefQuestion}/`;
    } else {
      const lastDetailsSteps = last(detailsSteps).name;
      prevUrl = `/start-a-project/${projectData.id}/details/${lastDetailsSteps}/`;
    }

    router.push(prevUrl);
  }

  onNextStep() {
    const { router, projectData, projectID, setBannerError, fetchProject, subCategory, category } = this.props

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: category.slug,
      subCategorySlug: subCategory.slug,
      fromStep: "review",
      toStep: null,
      sectionName: "review",
      fromIndex: 0,
      toIndex: null,
      questionsInSection: 1,
      direction: "forward"
    });

    trackClickWithMixpanel(MP_CHANGED_PAP_SECTION_EVENT_NAME, {
      categorySlug: category.slug,
      subCategorySlug: subCategory.slug,
      fromSectionName: "review",
      toSectionName: "payment",
      direction: "forward"
    });

    /* if the project is 1-1, start negotiation. Otherwise move on to payment step. */
    if (projectData.is_one_to_one) {
      this.start1to1Negotiation().then(() => {
        /* reload project with 1-1 updates */
        fetchProject(projectID)
      }).catch((err) => setBannerError('Error starting 1-1 project negotiation',
                                       'Please try again'))
      return
    }

    this.props.router.push(`/start-a-project/${projectID}/payment`);
  }

  editSection(sectionType, sectionData, value, event) {
    event.preventDefault();
    this.props.editSection(sectionType, sectionData, value);
  }

  getBriefSectionValues(section, editing=false) {
    /* get values for a given brief section */
    const { briefAnswers, attachments } = this.props

    /* file uploads are stored separately, outside of brief answers */
    if (section.name == 'file_upload') {
      return {'file_upload': attachments}
    }

    /* defaults */
    if (editing) {
      return pick(briefAnswers, [section.name])
    }
    return briefAnswers[section.name]
  }

  validateField(field, value, validationProps={}) {
    let validationError = '';
    const validationFuncs = getFieldPropsFromDefinition(field).fieldValidate || [];
    validationFuncs.forEach((validationFunc) => {
      validationError = validationFunc(value, {}, validationProps);
    });
    return validationError;
  }

  validateBriefQuestion(question) {
    const { briefQuestions } = this.props;
    const value = this.getBriefSectionValues({ name: question.name });
    const validationError = this.validateField(
      question, value, { briefQuestions, stepName: question.name }
    );
    return validationError? 'Please complete this question' : undefined;
  }

  validateTitle() {
    const step = this.props.detailsSteps.find(s => s.name === 'title');
    const value = this.getDetailsSectionValues({ name: 'title' }) || '';

    if (!step) {
      return;
    }

    const validationError = this.validateField(step, value);
    return validationError? 'Please provide a title' : undefined;
  }

  validateCost() {
    const projectData = this.props.projectData || {};
    return !projectData.original_package? 'Please choose a package' : undefined;
  }

  validateContract() {
    const step = this.props.detailsSteps.find(s => s.name === 'contract');
    const value = this.getDetailsSectionValues({ name: 'contract' }).contract;

    if (!step) {
      return;
    }

    const validationError = this.validateField(step, value);
    return validationError? 'Please provide contract information' : undefined;
  }

  renderProjectBriefReview() {
    const { briefQuestions, projectData, user } = this.props;

    return briefQuestions.map((step, index) => {
      const DisplayComponent = PAP_ANSWER_RENDERER_COMPONENTS[step.type];
      const rowClasses = ['multi-grid-select', 'color-selector'].includes(step.type)? 'multi-grid' : '';

      if (!DisplayComponent) return

      const section = { name: step.name, steps: [step] };

      const fieldProps = {
        fieldDefinition: step,
        value: this.getBriefSectionValues(section),
        userData: user.profile_data,
        projectData
      };

      const validationError = this.validateBriefQuestion(step);

      const editSection = this.editSection.bind(this, "brief", section,
                                                this.getBriefSectionValues(section, true))

      const classes = classnames("review-row", rowClasses, { "error": !!validationError });

      return (
        <div key={index}>
          { validationError && <span className="review-error-msg">{ validationError }</span> }
          <div className={classes}>
            <label className="review-label">{ step.answer_label }</label>
            <DisplayComponent {...fieldProps} />
            <a className="edit-button" onClick={editSection}>
              <i className="fa fa-pencil"></i> Edit
            </a>
          </div>
        </div>
      );
    });
  }

  getCostValues() {
    const { projectData } = this.props
    const budget = projectData.is_one_to_one? {
      'budget_option': (
        parseFloat(projectData.one_to_one_negotiation_value)? 'send_offer':'request_quote'
      ),
      'award_1_1': projectData.one_to_one_negotiation_value
    } : undefined

    return {
      'package': projectData.original_package,
      'budget': budget
    }
  }

  getContractValues() {
    const { customContract } = this.props
    const projectData = this.props.projectData || {};
    let values = {}

    if (customContract) {
      values = { isCustom: true, custom_contract: customContract }
    } else {
      values = pick(projectData, ['client_legal_name', 'client_legal_country'])
    }

    return {contract: values, projectId: projectData.id}
  }

  getDetailsSectionValues(section, editing=false) {
    /* Get values for a given details section */
    const projectData = this.props.projectData || {};

    if (section.name == 'cost') {
      return this.getCostValues()
    }

    if (section.name == 'contract') {
      return this.getContractValues()
    }

    /* defaults */
    if (editing) {
      return pick(projectData, [section.name])
    }
    return projectData[section.name]
  }

  renderProjectDetailsReview() {
    if (!this.props.projectData) {
      return null;
    }
    return this.props.detailsSections.map((section, index) => {
      const DisplayComponent = PAP_ANSWER_RENDERER_COMPONENTS[section.type];

      if (!DisplayComponent) return

      section['steps'] = this.props.detailsSteps.filter(
        (s) => section['stepsNames'].includes(s.name)
      )

      const fieldProps = {
        value: this.getDetailsSectionValues(section),
        projectData: this.props.projectData,
        awardValue1to1: this.props.awardValue1to1,
        packageName: this.props.packageName,
        user: this.props.user.profile_data
      };

      const editSection = this.editSection.bind(this, "details", section,
                                                this.getDetailsSectionValues(section, true));

      let validationError = '';
      if (section.name === 'title') {
        validationError = this.validateTitle();
      } else if (section.name === 'cost') {
        validationError = this.validateCost();
      } else if (section.name === 'contract') {
        validationError = this.validateContract();
      }

      return (
        <div key={index}>
          { validationError && <span className="review-error-msg">{ validationError }</span> }
          <div className={classnames("review-row", { "error": !!validationError })}>
            <label className="review-label">{ section.answer_label }</label>
            <DisplayComponent {...fieldProps} />
            <a className="edit-button" onClick={editSection}>
              <i className="fa fa-pencil"/>
              <span className="edit-button-label show-for-medium"> Edit</span>
            </a>
          </div>
        </div>
      );
    });
  }

  isValid() {
    const { briefQuestions } = this.props;

    const titleValid = !this.validateTitle();
    const costValid = !this.validateCost();
    const contractValid = !this.validateContract();
    const briefQuestionsValid = every(briefQuestions, q => !this.validateBriefQuestion(q));

    return !!(titleValid && costValid && contractValid && briefQuestionsValid);
  }

  render() {
    const {
      projectData, submitting, workflow: {editReviewSection}, creative1to1, briefAnswers
    } = this.props;

    if (editReviewSection) {
      return (
        <PAPEditReviewSection sectionData={editReviewSection.data} type={editReviewSection.type}
          {...this.props} />
      )
    }

    /* 1-1 project negotiation has just started */
    if (projectData && projectData.status == '1_1_negotiating') {
      return <Created1to1 creative={creative1to1} project={projectData}/>
    }

    const valid = this.isValid();

    return <PAPBase
      sectionName="review"
      extraClasses={[""]}
      loadingData={submitting || !projectData}
      currentStepIndex={0}
      currentSectionStepCount={0}
      onPrevStep={this.onPrevStep.bind(this)}
      onNextStep={this.onNextStep.bind(this)}
      nextEnabled={valid}
      prevEnabled={!!projectData}
      nextLabel={creative1to1? "Finish":""}
      currentFeatures={projectData? projectData.features : []}
      currentAwards={projectData? projectData.award_rules : []}
      currentTips={projectData? projectData.package_tips : []}
      billables={projectData? projectData.billables:null}
      {...this.props}>
        <div className="pap-form-step pap-review">
          <div className="row">
            <div className="columns medium-14 medium-offset-1 large-10 large-offset-3">
              <div className="pap-form-field">
                <div className="pap-question-header">
                  <h2 className="pap-field-label">Review Your Project</h2>
                  <p className="pap-field-description">
                    Make sure you're happy with your project before you purchase. You will
                    be able to add updates to your brief or add additional features at any point
                    in the project.
                  </p>
                  { !valid &&
                  <div className="callout alert">
                    Please make sure you fix all the errors below before proceeding.
                  </div> }
                </div>
                <div className="review-section">
                  <h3>Project Details</h3>
                  { this.renderProjectDetailsReview() }
                </div>
                <div className="review-section">
                  <h3>Creative Brief</h3>
                  { this.renderProjectBriefReview() }
                </div>
              </div>
            </div>
          </div>
        </div>
      </PAPBase>
    }
  }

export default PAPReview;



// WEBPACK FOOTER //
// ./pap/components/review.jsx